/**
 * Экология в реквизитах организации (файл заказчика 23.09.2026: «Блок Ответственный за экологию
 * перенести в реквизиты предприятия»).
 *
 * Секция собирает всё «экологическое», что относится к организации, а не к срокам документов:
 * контакт ответственного за экологию (правят и клиент, и менеджер) и, у клиента в «Настройках», —
 * «Экологический профиль» с площадками для просмотра. Обзор раздела грузится ОДИН раз на секцию:
 * обе карточки берут данные из него.
 *
 * Один компонент на обе стороны: клиент — `ecologyClientApi` (скоуп из сессии), команда —
 * `teamEcologyApi(cid)` во вкладке «Реквизиты» карточки клиента.
 */
import { useEffect, useState } from 'react'
import { MapPin } from 'lucide-react'
import type { EcoOverview, EcoScopeApi, EcoSite } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ecoSiteCategoryLabel } from '@/lib/ecology'
import { AppLink } from '@/app/navigation'
import { Card } from '@/design-system/components/layout/card'
import { Badge } from '@/design-system/components/primitives/badge'
import { Hint } from '@/design-system/components/primitives/text'
import { Alert } from '@/design-system/components/feedback/alert'
import { EcoResponsibleCard } from './responsible-card'

/** Карточка «Экологический профиль»: площадки с адресом, кодом НВОС и категорией — только просмотр. */
function EcoProfileCard({ sites }: { sites: EcoSite[] }) {
  return (
    <Card header="Экологический профиль" className="mt-4">
      <Hint className="mb-3">
        Категория НВОС (I–IV) — «экологический класс» площадки: чем она выше, тем больше отчётности и
        контроля. Сроки и документы — в разделе «Экология».
      </Hint>
      <ul>
        {sites.map((site) => (
          <li
            key={site.id}
            className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5 border-b border-line py-3 last:border-b-0"
          >
            <div className="flex min-w-0 items-start gap-2">
              <MapPin
                className="mt-0.5 size-4 shrink-0 stroke-current text-txt-tertiary"
                aria-hidden
              />
              <div className="min-w-0">
                <div className="font-medium text-txt">{site.name}</div>
                <div className="text-12 text-txt-secondary">
                  {[site.address, site.nvosCode ? `код НВОС ${site.nvosCode}` : '']
                    .filter(Boolean)
                    .join(' · ') || 'адрес не указан'}
                </div>
              </div>
            </div>
            <Badge variant={site.category === '' ? 'amber' : 'gray'}>
              {ecoSiteCategoryLabel(site.category)}
            </Badge>
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <AppLink to="/ecology" className="text-13 font-medium">
          Открыть раздел «Экология»
        </AppLink>
      </div>
    </Card>
  )
}

export function EcoRequisites({
  api,
  /** Показать «Экологический профиль» с площадками — нужен клиенту в «Настройках»; у команды
   *  площадки и так видны во вкладке «Экология» карточки клиента. */
  withSites = false,
}: {
  api: EcoScopeApi
  withSites?: boolean
}) {
  const [overview, setOverview] = useState<EcoOverview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setError(null)
    api
      .overview(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setOverview(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить данные по экологии'))
      })
    return () => controller.abort()
  }, [api])

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
      </Alert>
    )
  }
  if (!overview) {
    return (
      <Card header="Ответственный за экологию" className="mt-4">
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      </Card>
    )
  }
  return (
    <>
      <EcoResponsibleCard
        api={api}
        responsible={overview.responsible}
        onSaved={(next) => setOverview(next)}
      />
      {withSites ? <EcoProfileCard sites={overview.sites} /> : null}
    </>
  )
}
